import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DrawerModule } from 'primeng/drawer';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { AgenticAiApiService } from '../../agentic-ai-api.service';
import {
  buildUsageDonutSegments,
  USER_QUOTA_AGENT_CHART_COLORS,
  USAGE_SUMMARY_AGENT_MULTI_OPTIONS,
  USER_QUOTA_USAGE_PERIOD_OPTIONS,
  usageAgentBarWidth,
  type UsageDonutSegment,
  type UsageSummary,
  type UsageSummaryDailyPoint,
  type UserQuotaAgentUsage,
  type UserQuotaUsagePeriod,
} from '../../token-allocation.models';
import { ToastService } from '../../../../shared/services/toast.service';
import { UserService } from '../../../../shared/services/user.service';
import { extractHttpErrorMessage } from '../../../../shared/utils/http-error.util';

interface UserFilterOption {
  userId: number;
  label: string;
}

interface DailyStackSegment {
  agentCode: string;
  agentName: string;
  tokens: number;
  color: string;
  heightPercent: number;
}

@Component({
  selector: 'app-usage-summary-drawer',
  standalone: true,
  imports: [
    FormsModule,
    DrawerModule,
    MultiSelectModule,
    ProgressSpinnerModule,
    TableModule,
    TooltipModule,
  ],
  templateUrl: './usage-summary-drawer.html',
  styleUrl: './usage-summary-drawer.scss',
})
export class UsageSummaryDrawer {
  readonly visible = input(false);
  readonly visibleChange = output<boolean>();

  readonly loading = signal(false);
  readonly summary = signal<UsageSummary | null>(null);
  readonly error = signal<string | null>(null);
  readonly period = signal<UserQuotaUsagePeriod>('this_month');
  readonly selectedUserIds = signal<number[]>([]);
  readonly selectedAgentCodes = signal<string[]>([]);

  readonly usagePeriodOptions = USER_QUOTA_USAGE_PERIOD_OPTIONS;
  readonly agentFilterOptions = USAGE_SUMMARY_AGENT_MULTI_OPTIONS;
  readonly userFilterOptions = signal<UserFilterOption[]>([]);

  private readonly api = inject(AgenticAiApiService);
  private readonly toast = inject(ToastService);
  private readonly user = inject(UserService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.onDrawerOpened();
      }
    });
  }

  onDrawerVisibleChange(open: boolean): void {
    this.visibleChange.emit(open);
    if (!open) {
      this.resetState();
    }
  }

  formatTokens(n: number): string {
    return Number(n || 0).toLocaleString();
  }

  agentChartColor(agentCode: string): string {
    return USER_QUOTA_AGENT_CHART_COLORS[agentCode] ?? '#94a3b8';
  }

  agentBarWidth(agent: UserQuotaAgentUsage, totalTokens: number): number {
    return usageAgentBarWidth(agent.tokens, totalTokens);
  }

  usageDonutSegments(metrics: UsageSummary): UsageDonutSegment[] {
    return buildUsageDonutSegments(metrics.byAgent, metrics.totalTokens, (code) =>
      this.agentChartColor(code),
    );
  }

  dailyStackSegments(day: UsageSummaryDailyPoint): DailyStackSegment[] {
    const total = Math.max(0, Number(day.totalTokens) || 0);
    if (total <= 0) {
      return [];
    }
    const summary = this.summary();
    const agents = summary?.byAgent ?? [];
    const segments: DailyStackSegment[] = [];
    for (const agent of agents) {
      const tokens = Math.max(0, Number(day.byAgent[agent.agentCode] ?? 0) || 0);
      if (tokens <= 0) {
        continue;
      }
      segments.push({
        agentCode: agent.agentCode,
        agentName: agent.agentName,
        tokens,
        color: this.agentChartColor(agent.agentCode),
        heightPercent: Math.max(2, Math.round((tokens / total) * 100)),
      });
    }
    return segments;
  }

  dailyColumnHeight(day: UsageSummaryDailyPoint, maxTotal: number): number {
    const total = Math.max(0, Number(day.totalTokens) || 0);
    const max = Math.max(1, Number(maxTotal) || 1);
    return Math.max(4, Math.round((total / max) * 180));
  }

  maxDailyTotal(summary: UsageSummary): number {
    let max = 0;
    for (const day of summary.dailyByAgent) {
      max = Math.max(max, Number(day.totalTokens) || 0);
    }
    return max;
  }

  dailyBarTooltip(day: UsageSummaryDailyPoint): string {
    return `${this.formatDayLabel(day.date)}: ${this.formatTokens(day.totalTokens)} tokens`;
  }

  onPeriodChange(period: UserQuotaUsagePeriod): void {
    if (this.period() === period) {
      return;
    }
    this.period.set(period);
    this.loadSummary();
  }

  onUserFilterChange(value: number[] | null): void {
    const next = this.normalizeSelectedUserIds(value);
    if (this.sameNumberArray(this.selectedUserIds(), next)) {
      return;
    }
    this.selectedUserIds.set(next);
    this.loadSummary();
  }

  onAgentFilterChange(value: string[] | null): void {
    const next = this.normalizeSelectedAgentCodes(value);
    if (this.sameStringArray(this.selectedAgentCodes(), next)) {
      return;
    }
    this.selectedAgentCodes.set(next);
    this.loadSummary();
  }

  formatDayLabel(isoDate: string): string {
    const trimmed = (isoDate ?? '').trim();
    if (!trimmed) {
      return '';
    }
    const parsed = new Date(`${trimmed.slice(0, 10)}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return trimmed.slice(0, 10);
    }
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  showUserFilter(): boolean {
    return this.user.isAdministrator() || Boolean(this.summary()?.isAdministrator);
  }

  private onDrawerOpened(): void {
    this.error.set(null);
    this.loadSummary();
  }

  private resetState(): void {
    this.loading.set(false);
    this.summary.set(null);
    this.error.set(null);
    this.period.set('this_month');
    this.selectedUserIds.set([]);
    this.selectedAgentCodes.set([]);
    this.userFilterOptions.set([]);
  }

  private normalizeSelectedUserIds(value: number[] | null | undefined): number[] {
    const seen = new Set<number>();
    const ids: number[] = [];
    for (const raw of value ?? []) {
      const id = Math.trunc(Number(raw));
      if (!Number.isFinite(id) || id <= 0 || seen.has(id)) {
        continue;
      }
      seen.add(id);
      ids.push(id);
    }
    return ids;
  }

  private normalizeSelectedAgentCodes(value: string[] | null | undefined): string[] {
    const seen = new Set<string>();
    const codes: string[] = [];
    for (const raw of value ?? []) {
      const code = String(raw ?? '').trim();
      if (!code || seen.has(code)) {
        continue;
      }
      seen.add(code);
      codes.push(code);
    }
    return codes;
  }

  private sameNumberArray(left: number[], right: number[]): boolean {
    if (left.length !== right.length) {
      return false;
    }
    return left.every((value, index) => value === right[index]);
  }

  private sameStringArray(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }
    return left.every((value, index) => value === right[index]);
  }

  private loadSummary(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api
      .getUsageSummary({
        period: this.period(),
        userIds: this.selectedUserIds(),
        agentCodes: this.selectedAgentCodes(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.summary.set(res);
          if (res.isAdministrator) {
            this.userFilterOptions.set(
              res.filterUsers.map((user) => ({
                userId: user.userId,
                label: user.userEmail
                  ? `${user.userName} (${user.userEmail})`
                  : user.userName,
              })),
            );
          }
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          const message =
            extractHttpErrorMessage(err) || 'Could not load usage summary.';
          this.error.set(message);
          this.toast.error(message);
          this.cdr.markForCheck();
        },
      });
  }
}
